import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';

import {
  CoreStyling,
  createRipple,
  RippleAnimation,
} from '@/components/common/Buttons/shared';
import { isAnchor } from '@/components/common/Buttons/utils';
import {
  determineDestination,
  VisuallyHidden,
} from '@/components/common/utils';

import type { ButtonProps } from '@/components/common/Buttons/shared';

export const TertiaryButton = (props: ButtonProps) => {
  const { componentTheme } = props;

  if (isAnchor(props)) {
    const { 'aria-label': ariaLabel, children, onMouseDown, ...rest } = props;

    const destination =
      typeof rest?.href === 'string' && determineDestination(rest?.href);

    return (
      <TertiaryBtnLink
        componentTheme={componentTheme}
        rel={destination.isExternalLink ? 'external noopener' : null}
        aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
        target={destination.target}
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        {...rest}
      >
        {children}
        {destination.isExternalLink && !ariaLabel && (
          <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
        )}
      </TertiaryBtnLink>
    );
  } else {
    const { onMouseDown, ...rest } = props;

    return (
      <TertiaryBtn
        componentTheme={componentTheme}
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        {...rest}
      />
    );
  }
};

const TertiaryBtnTheme = (componentTheme) => {
  switch (componentTheme) {
    case 'light':
      return css`
        color: var(--text-action);
        &:hover {
          background: var(--surface-primary-subtle);
          color: var(--text-action-dark);
        }
      `;
    case 'dark':
      return css`
        color: var(--text-inverse-strong);
        &:hover {
          background: var(--surface-inverse-primary);
        }
      `;
    default:
      return '';
  }
};

const TertiaryBtnLink = styled(Link, { shouldForwardProp: isPropValid })<{
  componentTheme: 'light' | 'dark';
  icon?: 'left' | 'right';
}>`
  ${CoreStyling};
  ${RippleAnimation('tertiary')};
  ${({ componentTheme }) => css`
    ${TertiaryBtnTheme(componentTheme)}
  `}
`;

const TertiaryBtn = styled.button<{ componentTheme: 'light' | 'dark' }>`
  background: var(--transparent);
  border: none;
  ${CoreStyling};
  ${RippleAnimation('tertiary')};
  ${({ componentTheme }) => css`
    ${TertiaryBtnTheme(componentTheme)}
  `}
  cursor: pointer;
`;
